import { Injectable } from '@angular/core';
import { Apiservice } from './ApiService';
import { Observable, map } from 'rxjs';
import {
  ApiVenue,
  Venue,
  VenueEntry,
  VenueCityGroup,
  VenueContinentGroup,
  VENUE_OTHER_GROUP,
} from '../models/venue-admin.model';

/** Default rolling window (hours) used to decide which venues are "scanning now". */
const ACTIVE_SCAN_WINDOW_HOURS = 2;

@Injectable({
  providedIn: 'root',
})
export class VenueService {
  constructor(private apiService: Apiservice) {}

  getVenueList(): Observable<VenueEntry[]> {
    return this.apiService.getVenue().pipe(
      map((result: Record<string, ApiVenue>) => {
        return Object.keys(result)
          .map((key) => ({ key, value: normalizeVenue(result[key]) }))
          .sort((a, b) => a.value.name.localeCompare(b.value.name));
      })
    );
  }

  /**
   * Returns the set of venue keys that have scanned within the rolling window
   * (default 2 hours). Used to populate the "Scanning now" group. An empty set
   * is valid and means nobody is currently active.
   */
  getActiveScanVenueIds(windowHours: number = ACTIVE_SCAN_WINDOW_HOURS): Observable<Set<string>> {
    const since = Math.floor(Date.now() / 1000) - windowHours * 60 * 60;
    return this.apiService.getActiveScans(since).pipe(
      map((result) => new Set(Object.keys(result ?? {})))
    );
  }

  /**
   * Groups a flat venue list into a continent -> city -> venues hierarchy.
   * Venues missing continent or city are placed under an "Other" group so they
   * remain selectable. Continents, cities, and venues are each sorted by name.
   */
  buildVenueTree(venues: VenueEntry[]): VenueContinentGroup[] {
    const continents = new Map<string, Map<string, VenueEntry[]>>();

    for (const entry of venues) {
      const continent = entry.value.continent?.trim() || VENUE_OTHER_GROUP;
      const city = entry.value.city?.trim() || VENUE_OTHER_GROUP;

      let cities = continents.get(continent);
      if (cities == null) {
        cities = new Map<string, VenueEntry[]>();
        continents.set(continent, cities);
      }

      const cityVenues = cities.get(city);
      if (cityVenues == null) {
        cities.set(city, [entry]);
      } else {
        cityVenues.push(entry);
      }
    }

    return Array.from(continents.entries())
      .map(([continent, cities]): VenueContinentGroup => ({
        continent,
        cities: Array.from(cities.entries())
          .map(([city, cityVenues]): VenueCityGroup => ({
            city,
            venues: cityVenues.sort((a, b) => a.value.name.localeCompare(b.value.name)),
          }))
          .sort(byOtherLast((group) => group.city)),
      }))
      .sort(byOtherLast((group) => group.continent));
  }

  objectKeys(obj: any): string[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.keys(obj);
  }
}

/**
 * Maps a raw API venue to the internal `Venue` shape. The `/venue` endpoint
 * returns the geo fields as `Continent`/`City` (PascalCase); the rest of the
 * app uses camelCase `continent`/`city`, so we normalize once here.
 */
function normalizeVenue(apiVenue: ApiVenue): Venue {
  return {
    name: apiVenue.name,
    timezone: apiVenue.timezone,
    context: apiVenue.context,
    parents: apiVenue.parents,
    continent: apiVenue.Continent,
    city: apiVenue.City,
  };
}

/** Sort comparator that orders alphabetically but always pushes "Other" to the end. */
function byOtherLast<T>(label: (item: T) => string): (a: T, b: T) => number {
  return (a, b) => {
    const labelA = label(a);
    const labelB = label(b);
    if (labelA === VENUE_OTHER_GROUP) {
      return labelB === VENUE_OTHER_GROUP ? 0 : 1;
    }
    if (labelB === VENUE_OTHER_GROUP) {
      return -1;
    }
    return labelA.localeCompare(labelB);
  };
}
